import { Clipboard } from "@capacitor/clipboard";
import { useIonToast } from "@ionic/react";
import { useCallback } from "react";
import { useIntl } from "react-intl";

import AlertPopover from "@components/popovers/AlertPopover";
import Popover from "@components/popovers/Popover";
import PopoverItem from "@components/popovers/PopoverItem";
import styles from "@components/profile/Profile.module.css";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import { Opener } from "@hooks/useOpener";
import { fiveMinutesInMs } from "@typing/Constants";
import {
  CareTeamInviteCancelDocument,
  CareTeamInviteCancelMutation,
  CareTeamInviteCancelMutationVariables,
  CareTeamInviteResendDocument,
  CareTeamInviteResendMutation,
  CareTeamInviteResendMutationVariables,
  GetJourneyWithCareProfileQuery
} from "@typing/Generated";
import { IconList } from "@utils/iconUtils";

type Journey = GetJourneyWithCareProfileQuery["journey"];
type CareTeamInviteType = NonNullable<Journey["careTeamInvites"]>[0];

type Props = {
  careTeamInvite: CareTeamInviteType;
  onCancel?: () => void;
  opener: Opener;
};

const CareTeamInvitePopover = ({ careTeamInvite, onCancel, opener }: Props) => {
  const [presentToast, dismissToast] = useIonToast();
  const intl = useIntl();

  const [cancelCareTeamInvite] = useMutationWithValidation<
    CareTeamInviteCancelMutation,
    CareTeamInviteCancelMutationVariables
  >(CareTeamInviteCancelDocument, "careTeamInvite", {
    update: (store, { data }) => {
      store.modify({
        fields: {
          canceled() {
            return true;
          }
        },
        id: store.identify({ ...data?.careTeamInviteCancel?.careTeamInvite })
      });
    }
  });

  const [resendCareTeamInvite] = useMutationWithValidation<
    CareTeamInviteResendMutation,
    CareTeamInviteResendMutationVariables
  >(CareTeamInviteResendDocument, "careTeamInvite");

  const handleCancelCareTeamInvite = () => {
    cancelCareTeamInvite({ id: careTeamInvite.id }).then(() => {
      onCancel?.();
    });
    opener.close();
  };

  const handleCopyInvite = useCallback(() => {
    if (careTeamInvite.link) {
      Clipboard.write({ string: careTeamInvite.link });
      opener.close();
    }
  }, [careTeamInvite.link, opener]);

  const handleResendCareTeamInvite = useCallback(() => {
    if (careTeamInvite.lastSentAt && new Date().getTime() - careTeamInvite.lastSentAt.getTime() > fiveMinutesInMs) {
      resendCareTeamInvite({ id: careTeamInvite.id }).then(response => {
        if (response?.careTeamInviteResend?.careTeamInvite) {
          presentToast({
            buttons: [{ handler: () => dismissToast(), icon: IconList.xmark }],
            color: "primary-500",
            cssClass: "toast",
            duration: 5000,
            message: intl.formatMessage({ id: "pages.client.careProfile.careTeam.resentToast" }),
            position: "top"
          });
        }

        opener.close();
      });
    } else {
      presentToast({
        buttons: [{ handler: () => dismissToast(), icon: IconList.xmark }],
        cssClass: styles.toastWider,
        duration: 5000,
        message: intl.formatMessage({ id: "pages.client.careProfile.careTeam.resentInvalidToast" }),
        position: "top"
      });
      opener.close();
    }
  }, [careTeamInvite.lastSentAt, careTeamInvite.id, resendCareTeamInvite, opener, presentToast, intl, dismissToast]);

  return (
    <Popover isOpen={opener.isOpen} opener={opener}>
      <PopoverItem icon="trash" onClick={handleCancelCareTeamInvite} textKey="pages.profile.cancelInvite" />
      <AlertPopover textKey="pages.client.careProfile.careTeam.copied">
        <PopoverItem icon="copy" onClick={handleCopyInvite} textKey="pages.client.careProfile.careTeam.copy" />
      </AlertPopover>
      <PopoverItem icon="repeat" noBorder onClick={handleResendCareTeamInvite} textKey="pages.profile.resendInvite" />
    </Popover>
  );
};

export default CareTeamInvitePopover;
